/**
 * NumInput — Szám input ami engedi a mező kiürítését.
 * type="text" + inputMode="decimal" → mobilon számbillentyűzet, de törölhető.
 */
import { useState, useEffect, useRef } from 'react';

interface NumInputProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: string;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export default function NumInput({ value, onChange, min, max, step, className, style, placeholder, onKeyDown }: NumInputProps) {
  const [text, setText] = useState(String(value));
  const focused = useRef(false);

  // Sync from parent when not focused
  useEffect(() => {
    if (!focused.current) {
      setText(String(value));
    }
  }, [value]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    // Allow empty, digits, dot, comma, minus
    if (raw !== '' && !/^-?[\d.,]*$/.test(raw)) return;
    setText(raw);

    const normalized = raw.replace(',', '.');
    const num = parseFloat(normalized);
    if (!isNaN(num)) {
      let clamped = num;
      if (min != null && clamped < min) clamped = min;
      if (max != null && clamped > max) clamped = max;
      onChange(clamped);
    }
  }

  function handleBlur() {
    focused.current = false;
    const normalized = text.replace(',', '.');
    const num = parseFloat(normalized);
    if (isNaN(num) || text === '') {
      const fallback = min != null && min > 0 ? min : 0;
      onChange(fallback);
      setText(String(fallback));
    } else {
      let clamped = num;
      if (min != null && clamped < min) clamped = min;
      if (max != null && clamped > max) clamped = max;
      onChange(clamped);
      setText(String(clamped));
    }
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={text}
      onChange={handleChange}
      onFocus={() => { focused.current = true; setText(text === '0' ? '' : text); }}
      onBlur={handleBlur}
      onKeyDown={onKeyDown}
      className={className || 'form-input'}
      style={{ textAlign: 'center', ...style }}
      placeholder={placeholder}
      step={step}
    />
  );
}
